import {
  readPersistedRebalancingCompletionProof,
  type PersistedRebalancingCompletionProof,
  type RebalancingDominantState,
} from './rebalancingCompletionProof';

export interface CorrectiveRecallProofItem {
  label: string;
  value: string;
}

export type CorrectiveRecallSurface = 'monitoring' | 'risk';
export type CorrectiveRecallFollowUpKey =
  | 'review-drift'
  | 'set-targets-first'
  | 'review-largest-drifts'
  | 'review-suggested-trades'
  | 'check-analytics';

export interface CorrectiveRecallProof {
  kind: 'goals' | 'rebalancing-targets' | 'rebalancing-review';
  sourceLabel: 'Goals' | 'Rebalancing targets' | 'Rebalancing review';
  proofLabel: string;
  proofOccurredAt: string | null;
  timestampLabel: 'Updated' | 'Saved' | 'Recorded' | 'Last confirmed';
  followUpKey: CorrectiveRecallFollowUpKey | null;
  followUpLabel: string | null;
  followUpPath: string | null;
  tone: 'info' | 'success';
  message: string;
  proofItems: CorrectiveRecallProofItem[];
}

interface PersistedGoalsCorrectiveProof {
  goalId: string;
  goalName: string;
  updatedAt: string;
  rebalanceThresholdPct?: number;
  allocationCount: number;
  driftedAssetCount: number;
  maxDriftSymbol: string | null;
  maxDriftPct: number | null;
  reviewDriftHref: string;
}

function parseProofTimestamp(value?: string | null) {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

function formatProofTimestamp(value?: string | null) {
  if (!value) return 'Exact proof time unavailable';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Exact proof time unavailable';
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getRebalancingSourceLabel(kind: CorrectiveRecallProof['kind']) {
  return kind === 'rebalancing-targets' ? 'Rebalancing targets' : 'Rebalancing review';
}

function readPersistedGoalsCorrectiveProof(userId?: string | null): PersistedGoalsCorrectiveProof | null {
  if (!userId || typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(`portfolio-goals:active-proof:${userId}`);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<PersistedGoalsCorrectiveProof>;
    if (
      typeof parsed.goalId !== 'string' ||
      typeof parsed.goalName !== 'string' ||
      typeof parsed.updatedAt !== 'string' ||
      typeof parsed.reviewDriftHref !== 'string'
    ) {
      return null;
    }

    return {
      goalId: parsed.goalId,
      goalName: parsed.goalName,
      updatedAt: parsed.updatedAt,
      rebalanceThresholdPct:
        typeof parsed.rebalanceThresholdPct === 'number' ? parsed.rebalanceThresholdPct : undefined,
      allocationCount: typeof parsed.allocationCount === 'number' ? parsed.allocationCount : 0,
      driftedAssetCount: typeof parsed.driftedAssetCount === 'number' ? parsed.driftedAssetCount : 0,
      maxDriftSymbol: typeof parsed.maxDriftSymbol === 'string' ? parsed.maxDriftSymbol : null,
      maxDriftPct: typeof parsed.maxDriftPct === 'number' ? parsed.maxDriftPct : null,
      reviewDriftHref: parsed.reviewDriftHref,
    };
  } catch {
    return null;
  }
}

function normalizeGoalProof(proof: PersistedGoalsCorrectiveProof): CorrectiveRecallProof {
  const largestDriftValue =
    proof.maxDriftSymbol && proof.maxDriftPct !== null
      ? `${proof.maxDriftSymbol} • ${proof.maxDriftPct > 0 ? '+' : ''}${proof.maxDriftPct.toFixed(1)}pp`
      : 'No named drift recorded';

  return {
    kind: 'goals',
    sourceLabel: 'Goals',
    proofLabel: `${proof.goalName} remains the saved corrective plan on record`,
    proofOccurredAt: proof.updatedAt,
    timestampLabel: 'Updated',
    followUpKey: 'review-drift',
    followUpLabel: 'Review Drift',
    followUpPath: proof.reviewDriftHref,
    tone: 'info',
    message:
      proof.driftedAssetCount > 0
        ? `${proof.goalName} already has saved corrective proof on Goals. Keep that plan mounted and review the remaining drift instead of starting over.`
        : `${proof.goalName} already has saved corrective proof on Goals. Keep the recorded plan mounted and confirm drift against it instead of reopening setup prompts.`,
    proofItems: [
      { label: 'Source', value: 'Goals' },
      { label: 'Updated', value: formatProofTimestamp(proof.updatedAt) },
      { label: 'Outside tolerance', value: proof.driftedAssetCount.toString() },
      { label: 'Allocations', value: proof.allocationCount.toString() },
      ...(proof.rebalanceThresholdPct !== undefined
        ? [{ label: 'Threshold', value: `${proof.rebalanceThresholdPct.toFixed(1)}%` }]
        : []),
      { label: 'Largest drift', value: largestDriftValue },
    ],
  };
}

function getRebalancingFollowUp(state: RebalancingDominantState, surface: CorrectiveRecallSurface) {
  if (state === 'save-targets') {
    return {
      followUpKey: 'set-targets-first' as const,
      followUpLabel: 'Set targets first',
      followUpPath: '/rebalancing-targets',
      timestampLabel: 'Saved' as const,
      tone: 'info' as const,
      message:
        'Pinecurl already has saved corrective context for this portfolio. Keep the recorded proof mounted and finish target setup instead of restarting the drift loop.',
    };
  }

  if (state === 'review-largest-drifts') {
    return {
      followUpKey: 'review-largest-drifts' as const,
      followUpLabel: 'Review largest drifts',
      followUpPath: '/rebalancing',
      timestampLabel: 'Recorded' as const,
      tone: 'info' as const,
      message:
        'Pinecurl already recorded the latest rebalancing proof. Keep the highest-priority drift mounted before asking for another setup pass.',
    };
  }

  if (state === 'review-suggested-trades') {
    return {
      followUpKey: 'review-suggested-trades' as const,
      followUpLabel: 'Review suggested trades',
      followUpPath: '/rebalancing',
      timestampLabel: 'Recorded' as const,
      tone: 'info' as const,
      message:
        'Pinecurl already recorded the latest rebalancing proof. Keep the top advisory trade mounted instead of sending the user back to a generic restart prompt.',
    };
  }

  return {
    followUpKey: surface === 'monitoring' ? ('check-analytics' as const) : null,
    followUpLabel: surface === 'monitoring' ? 'Check Analytics' : null,
    followUpPath: surface === 'monitoring' ? '/portfolio-analytics' : null,
    timestampLabel: 'Last confirmed' as const,
    tone: 'success' as const,
    message:
      'Pinecurl already last confirmed this portfolio is within tolerance against the saved target mix. Keep that completion proof mounted instead of reviving the completed corrective action.',
  };
}

function normalizeRebalancingProof(
  proof: PersistedRebalancingCompletionProof,
  kind: CorrectiveRecallProof['kind'],
  surface: CorrectiveRecallSurface,
): CorrectiveRecallProof | null {
  if (!proof.proofLabel) {
    return null;
  }

  const followUp = getRebalancingFollowUp(proof.dominantState, surface);
  const sourceLabel = getRebalancingSourceLabel(kind);
  const priorityValue =
    proof.dominantState === 'review-suggested-trades'
      ? proof.topSuggestedTradeSymbol
        ? `${proof.topSuggestedTradeAction ?? 'REVIEW'} ${proof.topSuggestedTradeSymbol}`
        : 'No advisory trade recorded'
      : proof.topPrioritySymbol
        ? `${proof.topPrioritySymbol}${proof.topPriorityDriftPct !== undefined ? ` • ${proof.topPriorityDriftPct > 0 ? '+' : ''}${proof.topPriorityDriftPct.toFixed(1)}pp` : ''}`
        : 'No named drift recorded';

  return {
    kind,
    sourceLabel,
    proofLabel: proof.proofLabel,
    proofOccurredAt: proof.proofOccurredAt ?? proof.targetUpdatedAt ?? null,
    timestampLabel: followUp.timestampLabel,
    followUpKey: followUp.followUpKey,
    followUpLabel: followUp.followUpLabel,
    followUpPath: followUp.followUpPath,
    tone: followUp.tone,
    message: followUp.message,
    proofItems: [
      { label: 'Source', value: sourceLabel },
      {
        label: followUp.timestampLabel,
        value: formatProofTimestamp(proof.proofOccurredAt ?? proof.targetUpdatedAt ?? null),
      },
      { label: 'Outside tolerance', value: proof.afterAssetsWithDrift.toString() },
      { label: 'Target coverage', value: `${proof.targetCoverageCount}/${proof.trackedAssetCount}` },
      { label: 'Priority', value: priorityValue },
    ],
  };
}

function compareRecallPriority(
  left: CorrectiveRecallProof,
  right: CorrectiveRecallProof,
  surface: CorrectiveRecallSurface,
) {
  const timestampDelta = parseProofTimestamp(right.proofOccurredAt) - parseProofTimestamp(left.proofOccurredAt);
  if (timestampDelta !== 0) {
    return timestampDelta;
  }

  if (surface === 'monitoring') {
    if (left.followUpKey === 'review-drift' && right.followUpKey !== 'review-drift') return -1;
    if (right.followUpKey === 'review-drift' && left.followUpKey !== 'review-drift') return 1;
  }

  if (left.kind.startsWith('rebalancing') && !right.kind.startsWith('rebalancing')) return -1;
  if (right.kind.startsWith('rebalancing') && !left.kind.startsWith('rebalancing')) return 1;

  return 0;
}

export function resolveFreshestCorrectiveRecall(
  userId: string | null | undefined,
  surface: CorrectiveRecallSurface,
): CorrectiveRecallProof | null {
  if (!userId) {
    return null;
  }

  const candidates = [
    (() => {
      const proof = readPersistedGoalsCorrectiveProof(userId);
      return proof ? normalizeGoalProof(proof) : null;
    })(),
    (() => {
      const proof = readPersistedRebalancingCompletionProof('rebalancing-targets', userId);
      return proof ? normalizeRebalancingProof(proof, 'rebalancing-targets', surface) : null;
    })(),
    (() => {
      const proof = readPersistedRebalancingCompletionProof('rebalancing', userId);
      return proof ? normalizeRebalancingProof(proof, 'rebalancing-review', surface) : null;
    })(),
  ].filter((candidate): candidate is CorrectiveRecallProof => Boolean(candidate));

  if (!candidates.length) {
    return null;
  }

  return [...candidates].sort((left, right) => compareRecallPriority(left, right, surface))[0] ?? null;
}
